import { useRef } from 'react';
import { useScrollAffordance } from './useScrollAffordance';
import {
  Slide,
  SlideData,
  Theme,
  TextSlideData,
  CoverSlideData,
  ImageSlideData,
  VideoSlideData,
  QuizSlideData,
  WebappSlideData,
  AttuneSlideData,
  CodeSlideData,
  CanvaSlideData,
  ActivitySlideData,
  ReflectionSlideData,
  RevealPredictSlideData,
  ConfidenceMeterSlideData,
  ReflectActSlideData,
  BeforeAfterBridgeSlideData,
  SpotTheErrorSlideData,
  ClaimEvidenceSlideData,
  GalleryWalkSlideData,
  PerspectiveLensesSlideData,
  ScaffoldedInvestigationSlideData,
  LayeredRevealSlideData,
  TimelineNavigatorSlideData,
  ProgressiveBuilderSlideData,
  GuidedSandboxSlideData,
  DragSortSlideData,
  TeachBackSlideData,
  ComposedExperienceSlideData,
  TensionMapperSlideData,
  ScenarioSimulationSlideData,
  HotspotExplorerSlideData,
  SourceExaminationSlideData,
  MarkThisAnswerSlideData,
  EssayMarkingSlideData,
  RevisionExamSlideData,
  ReviseRunSlideData,
  BridgeSlideData,
  GeneratedInteractiveSlideData,
  migrateSlideData,
} from './slideTypes';
import { ActivityResponsePayload } from '../../../types';
import {
  RICH_TEXT_STYLES,
  SlideContainer,
  SubmissionFeedbackBanner,
} from './shared/SlideShared';
import type { SlideSubmissionFeedback } from './shared/SlideShared';
import { SkinFrame } from './skins/SkinFrame';
import './slide-responsive.css';

// Template components
import { CoverSlide } from './templates/CoverSlide';
import { TextSlide } from './templates/TextSlide';
import { ImageSlide } from './templates/ImageSlide';
import { VideoSlide } from './templates/VideoSlide';
import { QuizSlide } from './templates/QuizSlide';
import { WebappSlide } from './templates/WebappSlide';
import { AttuneSlide } from './templates/AttuneSlide';
import type { AttuneCourseContext } from '../../../lib/attune/courseContext';
import { StaleClientSlide } from './StaleClientSlide';
import { CodeSlide } from './templates/CodeSlide';
import { CanvaSlide } from './templates/CanvaSlide';
import { ReflectionSlide } from './templates/ReflectionSlide';
import { ActivitySlide } from './templates/ActivitySlide';
import { RevealPredictSlide } from './templates/scenario/RevealPredictSlide';
import { ConfidenceMeterSlide } from './templates/scenario/ConfidenceMeterSlide';
import { ReflectActSlide } from './templates/scenario/ReflectActSlide';
import { BeforeAfterBridgeSlide } from './templates/scenario/BeforeAfterBridgeSlide';
import { SpotTheErrorSlide } from './templates/scenario/SpotTheErrorSlide';
import { ClaimEvidenceSlide } from './templates/scenario/ClaimEvidenceSlide';
import { GalleryWalkSlide } from './templates/scenario/GalleryWalkSlide';
import { PerspectiveLensesSlide } from './templates/scenario/PerspectiveLensesSlide';
import { ScaffoldedInvestigationSlide } from './templates/scenario/ScaffoldedInvestigationSlide';
import { LayeredRevealSlide } from './templates/scenario/LayeredRevealSlide';
import { TimelineNavigatorSlide } from './templates/scenario/TimelineNavigatorSlide';
import { ProgressiveBuilderSlide } from './templates/scenario/ProgressiveBuilderSlide';
import { GuidedSandboxSlide } from './templates/scenario/GuidedSandboxSlide';
import { DragSortSlide } from './templates/scenario/DragSortSlide';
import { TeachBackSlide } from './templates/scenario/TeachBackSlide';
import { ComposedExperienceSlide } from './templates/scenario/ComposedExperienceSlide';
import { TensionMapperSlide } from './templates/scenario/TensionMapperSlide';
import { ScenarioSimulationSlide } from './templates/scenario/ScenarioSimulationSlide';
import { HotspotExplorerSlide } from './templates/scenario/HotspotExplorerSlide';
import { SourceExaminationSlide } from './templates/scenario/SourceExaminationSlide';
import { MarkThisAnswerSlide } from './templates/scenario/MarkThisAnswerSlide';
import { EssayMarkingSlide } from './templates/EssayMarkingSlide';
import { RevisionExamSlide } from './templates/scenario/RevisionExamSlide';
import { ReviseRunSlide } from './templates/revise/ReviseRunSlide';
import { BridgeSlide } from './templates/scenario/BridgeSlide';
import { GeneratedInteractiveSlide } from './templates/GeneratedInteractiveSlide';
import {
  TierBAtmosphere,
  shouldWrapInAtmosphere,
} from '../../../lib/scenario-themes/TierBAtmosphere';

// Re-export for consumers (SlideBasedViewer imports EmbedEventHandlers from here)
export type { EmbedEventHandlers } from './shared/SlideShared';
import type { EmbedEventHandlers } from './shared/SlideShared';

interface SlideCanvasProps {
  slide: Slide;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: SlideData) => void;
  embedHandlers?: EmbedEventHandlers;
  lessonId?: string;
  courseId?: string;
  onSlideCompleted?: (slideId: string) => void;
  /** Advance to the next lesson slide — used by the Attune close hand-off. */
  onAdvance?: () => void;
  /** Close CTA label when advancing means finishing the lesson/course (Attune). */
  advanceLabel?: string;
  onActivityResponse?: (slideId: string, response: ActivityResponsePayload) => void;
  onReflectionSave?: (slideId: string, answers: Record<string, string>) => void;
  onSubmission?: (slideId: string, submissionType: string, submissionData: unknown) => void;
  savedReflectionAnswers?: Record<string, string>;
  /**
   * For scenario templates: the learner's last recorded response payload
   * (the `responseData` they previously submitted for this slide). Lets a
   * returning learner see their completed activity instead of a blank one.
   * Provided by the lesson viewer; undefined in the builder/editing context.
   */
  scenarioSavedResponse?: Record<string, unknown> | null;
  submissionFeedback?: SlideSubmissionFeedback;
  /**
   * For BridgeSlide: context about the prior scenario slide in the same group
   * so the bridge can render a response-aware callout. Provided by the lesson
   * viewer; ignored by every other template.
   */
  priorSlideContext?: {
    templateId: string;
    response: Record<string, unknown> | null | undefined;
  };
  /** Lesson context slices for an Attune slide (viewer-supplied). previousSlide
   *  is the slide the learner just left — the facilitator renders it frosted
   *  behind the conversation ("through glass"). */
  attuneContext?: { before: string; after: string; whole: string; momentIndex: number; autoMode?: 'facilitator' | 'experience'; previousSlide?: Slide | null };
  /** Course-level context for an Attune slide in the BUILDER preview, where
   *  courseId/lessonId are withheld (no memory, no capture) but the moment must
   *  still know the real course. Ignored when the ids are supplied. */
  attuneCourseContext?: AttuneCourseContext | null;
  /** Attune: true when this slide is already completed (viewer-supplied), so
   *  re-entry shows a revisit card instead of re-running the moment. */
  alreadyCompleted?: boolean;
  /** Activity Doors: readable text of the lesson slides BEFORE a
   *  generated-interactive slide (builder-supplied). Sent to the build only
   *  when the door's intake opted into lesson source. */
  doorLessonSource?: string;
}

// ============================================================
// Main SlideCanvas — renders the appropriate template
// ============================================================
export function SlideCanvas({ slide, theme, isEditing, onUpdate, embedHandlers, lessonId, courseId, onSlideCompleted, onAdvance, advanceLabel, onActivityResponse, onReflectionSave, onSubmission, savedReflectionAnswers, scenarioSavedResponse, submissionFeedback, priorSlideContext, attuneContext, attuneCourseContext, alreadyCompleted, doorLessonSource }: SlideCanvasProps) {
  // Text-bearing templates: the ones that can outgrow the frame and so are
  // allowed to scroll in the viewer. (They used to be the templates a JS
  // fitting pass shrank to fit; that pass never worked and is gone — see
  // slide-responsive.css.)
  const overflowTemplates = new Set(['text', 'cover', 'quiz', 'activity', 'reflection', 'title', 'content', 'bullets', 'split', 'image-text', 'text-block']);
  const overflowEnabled = overflowTemplates.has(slide.data?.template || '');
  // Full-bleed templates have no growable text content — their root is `h-full`
  // chained down to an iframe or object-cover image. Under the editor's
  // `min-h-full` wrapper (height: auto) that chain collapses (iframes to the
  // 150px default, images to their natural aspect height), so these must keep
  // a definite `h-full` in the editor too.
  const fullBleedTemplates = new Set(['webapp', 'canva', 'video', 'attune', 'image', 'generated-interactive']);
  const fillsStage = fullBleedTemplates.has(slide.data?.template || '');
  const containerRef = useRef<HTMLDivElement | null>(null);

  /**
   * A text slide that still doesn't fit after the fitting system has spent
   * itself SCROLLS rather than being cut off.
   *
   * It used to clip, and clip silently: the student had no way to know a
   * paragraph was missing, and the author had no way to know either. Shrinking
   * alone cannot save every slide — on a phone the body's `clamp()` floor
   * (0.9rem) is already binding at full size, so the compression can only ever
   * take it out of the headings, and a long slide runs out of road.
   *
   * Scrolling is safe here in a way it would not have been before the nav
   * split: the rail is docked OUTSIDE this box, so the controls stay put while
   * the content moves, and the horizontal swipe defers to vertical travel
   * (see resolveSwipe's RATIO) so a scroll can't turn the page by accident.
   *
   * Restricted to the text templates. Full-bleed ones (webapp, canva, video,
   * attune, image) are a definite `h-full` chain down to an iframe or an
   * object-cover image and must not gain a scrollbar; the experience templates
   * manage their own scrolling internally.
   */
  const scrollsWhenLong = overflowEnabled && !isEditing;
  const showScrollCue = useScrollAffordance(containerRef, scrollsWhenLong);

  const renderSlide = () => {
    if (!slide.data) {
      return (
        <div className="h-full flex items-center justify-center" style={{ color: theme.colors.textMuted }}>
          Empty slide — no content data
        </div>
      );
    }

    const data = migrateSlideData(slide.data);

    switch (data.template) {
      case 'text':
        return (
          <TextSlide
            data={data as TextSlideData}
            theme={theme}
            isEditing={isEditing}
            onUpdate={(d) => onUpdate(d)}
          />
        );
      case 'cover':
        return (
          <CoverSlide
            data={data as CoverSlideData}
            theme={theme}
            isEditing={isEditing}
            onUpdate={(d) => onUpdate(d)}
          />
        );
      case 'image':
        return (
          <ImageSlide
            data={data as ImageSlideData}
            theme={theme}
            isEditing={isEditing}
            onUpdate={(d) => onUpdate(d)}
          />
        );
      case 'video':
        return (
          <VideoSlide
            data={data as VideoSlideData}
            theme={theme}
            isEditing={isEditing}
            onUpdate={(d) => onUpdate(d)}
          />
        );
      case 'quiz':
        // Skinnable (slide_skins Phase 4): SkinFrame adds world atmosphere only
        // when this slide carries its own themeOverride (written by the flagged
        // picker); a world-themed course without one renders untouched.
        return (
          <SkinFrame theme={theme} slideData={data} slideThemeOverride={slide.themeOverride}>
            <QuizSlide
              data={data as QuizSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onQuizAnswered={(result) => {
                onActivityResponse?.(slide.id, {
                  activityType: 'quiz',
                  mode: 'quiz',
                  responseData: { selectedOptionId: result.selectedOptionId },
                  isCorrect: result.isCorrect,
                  score: result.isCorrect ? 100 : 0,
                });
                onSlideCompleted?.(slide.id);
              }}
            />
          </SkinFrame>
        );
      case 'webapp':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <WebappSlide
              data={data as WebappSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              slideId={slide.id}
              lessonId={lessonId}
              courseId={courseId}
              embedHandlers={embedHandlers}
            />
          </>
        );
      case 'generated-interactive':
        return (
          <GeneratedInteractiveSlide
            data={data as GeneratedInteractiveSlideData}
            theme={theme}
            isEditing={isEditing}
            onUpdate={(d) => onUpdate(d)}
            slideId={slide.id}
            lessonId={lessonId}
            courseId={courseId}
            embedHandlers={embedHandlers}
            lessonSource={doorLessonSource}
          />
        );
      case 'attune':
        return (
          <AttuneSlide
            data={data as AttuneSlideData}
            theme={theme}
            isEditing={isEditing}
            onUpdate={(d) => onUpdate(d)}
            slideId={slide.id}
            lessonId={lessonId}
            courseId={courseId}
            attuneContext={attuneContext}
            courseContext={attuneCourseContext}
            onComplete={() => onSlideCompleted?.(slide.id)}
            onAdvance={onAdvance}
            advanceLabel={advanceLabel}
            alreadyCompleted={alreadyCompleted}
          />
        );
      case 'code':
        return (
          <CodeSlide
            data={data as CodeSlideData}
            theme={theme}
            isEditing={isEditing}
            onUpdate={(d) => onUpdate(d)}
          />
        );
      case 'canva':
        return (
          <CanvaSlide
            data={data as CanvaSlideData}
            theme={theme}
            isEditing={isEditing}
            onUpdate={(d) => onUpdate(d)}
          />
        );
      case 'activity':
        // Skinnable (slide_skins Phase 4) — see the 'quiz' case.
        return (
          <SkinFrame theme={theme} slideData={data} slideThemeOverride={slide.themeOverride}>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <ActivitySlide
              data={data as ActivitySlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => {
                onActivityResponse?.(slide.id, response);
                onSlideCompleted?.(slide.id);
                if (slide.submittable && onSubmission) {
                  onSubmission(slide.id, response.activityType, response.responseData);
                }
              }}
            />
          </SkinFrame>
        );
      case 'reflection':
        // Skinnable (slide_skins Phase 4) — see the 'quiz' case.
        return (
          <SkinFrame theme={theme} slideData={data} slideThemeOverride={slide.themeOverride}>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <ReflectionSlide
              data={data as ReflectionSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              isSubmittable={slide.submittable}
              savedAnswers={savedReflectionAnswers}
              onSave={onReflectionSave ? (answers) => {
                onReflectionSave(slide.id, answers);
                // Reflection is interaction-gated (Fix 1): count it satisfied
                // once the learner has written any non-empty answer.
                if (Object.values(answers).some((a) => a && a.trim().length > 0)) {
                  onSlideCompleted?.(slide.id);
                }
              } : undefined}
              onSubmit={onSubmission ? (answers) => onSubmission(slide.id, 'reflection', answers) : undefined}
            />
          </SkinFrame>
        );
      // --- Scenario templates (AI-built interactive experiences) ---
      // Each renders the SubmissionFeedbackBanner and is `submittable: true` in
      // the registry, but unlike `activity` none of them route to `onSubmission`.
      // TODO(submittable-scenarios): wiring instructor-gradable submissions needs
      // a per-template product decision — what text/artifact does an instructor
      // grade for a drag-sort vs a tension-mapper? Until that UX is defined we
      // pass only onActivityResponse + onComplete; the banner shows graded
      // feedback once an instructor leaves it through other channels.
      case 'reveal-predict':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <RevealPredictSlide
              data={data as RevealPredictSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'confidence-meter':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <ConfidenceMeterSlide
              data={data as ConfidenceMeterSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'reflect-act':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <ReflectActSlide
              data={data as ReflectActSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'before-after-bridge':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <BeforeAfterBridgeSlide
              data={data as BeforeAfterBridgeSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'spot-the-error':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <SpotTheErrorSlide
              data={data as SpotTheErrorSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'claim-evidence':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <ClaimEvidenceSlide
              data={data as ClaimEvidenceSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'source-examination':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <SourceExaminationSlide
              data={data as SourceExaminationSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
            />
          </>
        );
      case 'essay-marking':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <EssayMarkingSlide
              data={data as EssayMarkingSlideData}
              theme={theme}
              isEditing={isEditing}
              courseId={courseId}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
            />
          </>
        );
      case 'mark-this-answer':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <MarkThisAnswerSlide
              data={data as MarkThisAnswerSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
            />
          </>
        );
      case 'revision-exam':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <RevisionExamSlide
              data={data as RevisionExamSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
            />
          </>
        );
      case 'revise-run':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <ReviseRunSlide
              data={data as ReviseRunSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              onAdvance={onAdvance}
              slideId={isEditing ? undefined : slide.id}
              courseId={isEditing ? undefined : courseId}
              lessonId={isEditing ? undefined : lessonId}
            />
          </>
        );
      case 'gallery-walk':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <GalleryWalkSlide
              data={data as GalleryWalkSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'perspective-lenses':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <PerspectiveLensesSlide
              data={data as PerspectiveLensesSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'scaffolded-investigation':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <ScaffoldedInvestigationSlide
              data={data as ScaffoldedInvestigationSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'layered-reveal':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <LayeredRevealSlide
              data={data as LayeredRevealSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'timeline-navigator':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <TimelineNavigatorSlide
              data={data as TimelineNavigatorSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'progressive-builder':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <ProgressiveBuilderSlide
              data={data as ProgressiveBuilderSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'guided-sandbox':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <GuidedSandboxSlide
              data={data as GuidedSandboxSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'drag-sort':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <DragSortSlide
              data={data as DragSortSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'teach-back':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <TeachBackSlide
              data={data as TeachBackSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'composed-experience':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <ComposedExperienceSlide
              data={data as ComposedExperienceSlideData}
              theme={theme}
              isEditing={isEditing}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'tension-mapper':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <TensionMapperSlide
              data={data as TensionMapperSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'scenario-simulation':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <ScenarioSimulationSlide
              data={data as ScenarioSimulationSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
              onComplete={() => onSlideCompleted?.(slide.id)}
              savedResponse={scenarioSavedResponse}
            />
          </>
        );
      case 'hotspot-explorer':
        return (
          <>
            {submissionFeedback && !isEditing && (
              <SubmissionFeedbackBanner feedback={submissionFeedback} theme={theme} />
            )}
            <HotspotExplorerSlide
              data={data as HotspotExplorerSlideData}
              theme={theme}
              isEditing={isEditing}
              onUpdate={(d) => onUpdate(d)}
              onActivityResponse={(response) => onActivityResponse?.(slide.id, response)}
            />
          </>
        );
      case 'bridge':
        return (
          <BridgeSlide
            data={data as BridgeSlideData}
            theme={theme}
            isEditing={isEditing}
            onUpdate={(d) => onUpdate(d)}
            priorSlideContext={priorSlideContext}
          />
        );
      default:
        // Almost always a STALE CLIENT: content newer than the app bundle a
        // long-lived tab's service worker cached (e.g. a pre-Attune bundle
        // meeting an attune slide). Self-heal with a one-shot SW update +
        // reload instead of a dead "Unknown slide template".
        return (
          <StaleClientSlide
            template={String((data as { template?: string }).template ?? '')}
            theme={theme}
            isEditing={isEditing}
          />
        );
    }
  };

  // Tier B: on a theme-world, any template WITHOUT a bespoke renderer still gets
  // the world's atmosphere behind the standard render, so a world holds across a
  // whole lesson instead of only its two hero slides. Bespoke renderers mount
  // their own <WorldStage> and opt out. Skipped while editing — the builder
  // stage needs a plain, predictable surface to lay out against, and the
  // atmosphere is `overflow: hidden`, which fights the editor's growable
  // `min-h-full` content box.
  const template = slide.data?.template ?? '';
  const wrapInAtmosphere = !isEditing && shouldWrapInAtmosphere(theme.id, template);

  // The atmosphere wraps the scroll box, never sits inside it. It is
  // `overflow: hidden` and full-height, so between the scroller and its content
  // it would swallow the overflow and a long slide would stop scrolling — the
  // clipping this whole area just stopped doing. `tw-tierb-slot` marks the
  // content wrapper so the wrapper's CSS can clear the template root's own
  // opaque background (see TierBAtmosphere).
  //
  // The cue sits outside the scroll box for the same family of reason: anything
  // inside it scrolls away with the content it is meant to point at.
  const measured = (
    <div className="relative h-full w-full">
      <div
        ref={containerRef}
        className={`relative h-full w-full ${
          isEditing ? '' : scrollsWhenLong ? 'overflow-y-auto' : 'overflow-hidden'
        }`}
      >
        <div
          className={`${
            (isEditing && !fillsStage) || scrollsWhenLong ? 'min-h-full' : 'h-full'
          }${scrollsWhenLong ? ' slide-scroll-body' : ''} w-full${
            wrapInAtmosphere ? ' tw-tierb-slot' : ''
          }`}
        >
          {renderSlide()}
        </div>
      </div>
      {/* Scrolling with nothing to say so would be the old silent failure in a
          new costume — the student still cannot tell there is more. */}
      {showScrollCue && (
        <div
          className="slide-scroll-cue"
          aria-hidden="true"
          style={{ ['--slide-cue-bg' as string]: theme.colors.background }}
        />
      )}
    </div>
  );

  return (
    <>
      <style>{RICH_TEXT_STYLES}</style>
      <SlideContainer theme={theme} isEditing={isEditing}>
        {wrapInAtmosphere ? (
          <TierBAtmosphere theme={theme}>{measured}</TierBAtmosphere>
        ) : (
          measured
        )}
      </SlideContainer>
    </>
  );
}
